import create, { GetState, SetState } from 'zustand'
import {
  AvailabilityFilter,
  LibraryBrandingQueryResponse,
  Maybe,
  Patron,
  SplashConfig,
} from 'types/hoopla'

import { client } from 'graphql/client'
import config from 'config'
import {
  QueryGetEnabledAutoLend,
  QueryGetEnabledEmails,
  QueryGetLibrary,
  QueryGetLibraryBrandingQueryDocument,
  QueryGetLibraryCardUrl,
  QueryGetLibraryCirculationLimits,
  QueryGetLibraryCountryData,
  QueryGetLibraryHoldReservationHours,
  QueryGetLibraryPinRequired,
  QueryGetLibrarySettings,
  QueryGetLibrarySplashConfigQueryDocument,
} from 'state/useLibrary/useLibrary.graphql'
import type { LibraryExt } from 'types/hoopla'

type UseLibraryState = {
  circulationLimits: {
    maxConcurrentEstCirculations: number
    maxPPUCirculationsPerMonth: number
  }
  countryAbbrev?: string
  enableAutoLend?: boolean
  enabledEmails: {
    enableBorrowEmails?: boolean
    enableGeneralEmails?: boolean
  }
  holdReservationHours?: number
  isLibraryBrandingEnabled: boolean
  library?: {
    id: string
    name: string
    country: {
      id: string
    }
    city: string
  }
  libraryBrandingColor?: Maybe<string>
  libraryCardUrl?: string
  librarySettings?: {
    defaultAvailabilityFilter?: AvailabilityFilter
    enableHolds?: boolean
    enableEST?: boolean
    enablePPU?: boolean
    enableTitleRequests?: boolean
    enabled?: boolean
  }
  librarySplashSettings?: Maybe<SplashConfig> | undefined
  logoUrl?: Maybe<string>
  pinRequired?: boolean

  getDefaultAvailabilityFilter: () => AvailabilityFilter
  getLibrary: () => void
  getLibraryBranding: () => Promise<void>
  getLibraryCardUrl: () => void
  getLibraryCirculationLimits: () => void
  getLibraryCountry: () => void
  getLibraryEnableAutoLend: () => void
  getLibraryEnabledEmails: () => void
  getLibraryHoldReservationHours: () => void
  getLibraryPinRequired: () => void
  getLibrarySettings: () => void
  getLibrarySplash: () => void
  teardown: () => void
}

const defaultState = {
  circulationLimits: {
    maxConcurrentEstCirculations: 0,
    maxPPUCirculationsPerMonth: 0,
  },
  countryAbbrev: undefined,
  enableAutoLend: undefined,
  enabledEmails: {
    enableBorrowEmails: undefined,
    enableGeneralEmails: undefined,
  },
  holdReservationHours: undefined,
  isLibraryBrandingEnabled: false,
  library: undefined,
  libraryBrandingColor: undefined,
  libraryCardUrl: undefined,
  librarySettings: {
    defaultAvailabilityFilter: undefined,
    enableHolds: undefined,
    enableEST: undefined,
    enablePPU: undefined,
    enableTitleRequests: undefined,
    enabled: undefined,
  },
  librarySplashSettings: undefined,
  logoUrl: undefined,
  pinRequired: undefined,
}

export const useLibrary = create(
  (
    set: SetState<UseLibraryState>,
    get: GetState<UseLibraryState>,
  ): UseLibraryState => ({
    ...defaultState,

    getDefaultAvailabilityFilter: () => {
      const defaultAvailability = get().librarySettings
        ?.defaultAvailabilityFilter as AvailabilityFilter

      const libraryHasAvailability =
        Object.values(AvailabilityFilter).includes(defaultAvailability)

      if (libraryHasAvailability) return defaultAvailability

      return get().librarySettings?.enableTitleRequests
        ? AvailabilityFilter.AllHooplaTitles
        : AvailabilityFilter.AllTitles
    },

    getLibrary: async () => {
      const { data } = await client.query<{
        patron: { library: LibraryExt }
      }>({
        query: QueryGetLibrary,
        errorPolicy: 'all',
      })

      const library = data?.patron?.library
      set({ library })
    },

    getLibraryBranding: async () => {
      const { data } = await client.query<{
        libraryBranding: LibraryBrandingQueryResponse
      }>({
        query: QueryGetLibraryBrandingQueryDocument,
        errorPolicy: 'all',
      })

      const baseColor = data?.libraryBranding?.baseColor
      const logoFileName = data?.libraryBranding?.logoFileName

      // Return a null object when:
      //  enabled is false
      //  base color is false, not set, not right
      //  image file has not been set (or are not valid)
      //
      // Else return an object:
      //  baseColor as a hex color code
      //  logoFileName
      //  but excludes enabled and id
      const isLibraryBrandingEnabled = () => {
        if (data?.libraryBranding != null) {
          return true
        }
        return false
      }

      set({
        isLibraryBrandingEnabled: isLibraryBrandingEnabled(),
        libraryBrandingColor: baseColor ?? '',
        logoUrl: logoFileName
          ? `${config.librarySplashUrl}${logoFileName}`
          : '',
      })
    },

    getLibraryCardUrl: async () => {
      const { data } = await client.query<{
        patron: { library: { settings: { libraryCardUrl: string } } }
      }>({
        query: QueryGetLibraryCardUrl,
        errorPolicy: 'all',
      })

      const libraryCardUrl = data?.patron?.library?.settings?.libraryCardUrl
      set({ libraryCardUrl })
    },

    getLibraryCirculationLimits: async () => {
      const { data } = await client.query<{
        patron: {
          library: {
            settings: {
              maxConcurrentEstCirculations: number
              maxPPUCirculationsPerMonth: number
            }
          }
        }
      }>({
        query: QueryGetLibraryCirculationLimits,
        errorPolicy: 'all',
      })

      const maxConcurrentEstCirculations =
        data.patron?.library?.settings.maxConcurrentEstCirculations ?? 0
      const maxPPUCirculationsPerMonth =
        data?.patron?.library?.settings?.maxPPUCirculationsPerMonth ?? 0

      const circulationLimits = {
        maxConcurrentEstCirculations,
        maxPPUCirculationsPerMonth,
      }

      set({ circulationLimits })
    },

    getLibraryCountry: async () => {
      const { data } = await client.query<{
        patron: {
          library: { country: { abbrev: string; name: string } }
        }
      }>({
        query: QueryGetLibraryCountryData,
        errorPolicy: 'all',
      })

      const countryAbbrev = data?.patron?.library?.country.abbrev
      set({ countryAbbrev })
    },

    getLibraryEnableAutoLend: async () => {
      const { data } = await client.query<{
        patron: {
          library: {
            settings: {
              enableAutoLend: boolean
            }
          }
        }
      }>({
        query: QueryGetEnabledAutoLend,
        errorPolicy: 'all',
      })

      const enableAutoLend =
        data?.patron?.library?.settings?.enableAutoLend ?? false
      set({ enableAutoLend })
    },

    getLibraryEnabledEmails: async () => {
      const { data } = await client.query<{
        patron: {
          library: {
            settings: {
              enableBorrowEmails: boolean
              enableGeneralEmails: boolean
            }
          }
        }
      }>({
        query: QueryGetEnabledEmails,
        errorPolicy: 'all',
      })

      const enableBorrowEmails =
        data?.patron?.library?.settings?.enableBorrowEmails ?? false
      const enableGeneralEmails =
        data?.patron?.library?.settings?.enableGeneralEmails ?? false

      const enabledEmails = {
        enableBorrowEmails,
        enableGeneralEmails,
      }

      set({ enabledEmails })
    },

    getLibraryHoldReservationHours: async () => {
      const { data } = await client.query<{
        patron: { library: { settings: { holdReservationHours: number } } }
      }>({
        query: QueryGetLibraryHoldReservationHours,
        errorPolicy: 'all',
      })

      const holdReservationHours =
        data?.patron?.library?.settings?.holdReservationHours

      set({ holdReservationHours })
    },

    getLibraryPinRequired: async () => {
      const { data } = await client.query<{
        patron: { library: { settings: { pinRequired: boolean } } }
      }>({
        query: QueryGetLibraryPinRequired,
        errorPolicy: 'all',
      })

      const pinRequired = data?.patron?.library?.settings?.pinRequired ?? false
      set({ pinRequired })
    },

    getLibrarySettings: async () => {
      const { data } = await client.query<{
        patron: {
          library: {
            settings: {
              defaultAvailabilityFilter: AvailabilityFilter
              enableHolds: boolean
              enableEST: boolean
              enablePPU: boolean
              enableTitleRequests: boolean
              enabled: boolean
            }
          }
        }
      }>({
        query: QueryGetLibrarySettings,
        errorPolicy: 'all',
      })

      const librarySettings = data?.patron?.library?.settings

      set({ librarySettings })
    },

    getLibrarySplash: async () => {
      const { data } = await client.query<{ patron: Patron }>({
        query: QueryGetLibrarySplashConfigQueryDocument,
        errorPolicy: 'all',
      })
      const splashConfig = {
        ...data.patron.library.splashConfig,
      }
      set({ librarySplashSettings: splashConfig })
    },

    teardown: () => {
      set({ ...defaultState })
    },
  }),
)
